import { backendBaseUrl } from "src/domains/shared/api/calculation/backendBaseUrl";
import { getIdToken } from "src/domains/shared/utils/amplifyCompat";

/**
 * 프로젝트 소유권 이관 클라이언트 — main-api `POST /v1/projects/{project_id}/migration`(현 owner 만).
 * 멤버 CRUD(`usersOfProject.repository`)는 Amplify 게이트웨이를 쓰지만, 새 라우트는 projectHistory·worksite 와
 * 같은 raw fetch(`backendBaseUrl()` + `getIdToken()` Bearer) 관례를 따른다. 계약 SoT 는 backend-api Skill
 * 라이브 조회(transfer_owner_v1_projects__project_id__migration_post) — 이관 성공 시 구 owner 는 그 프로젝트의
 * 멤버에서 빠진다.
 */

/**
 * HTTP 에러 분기용 — 훅이 status 로 안내 문구를 가른다: 400 자기 자신·admin 계정·요청 형식 오류 /
 * 404 대상 이메일 미가입·탈퇴 또는 요청자가 그 프로젝트의 owner 아님(비owner·비멤버) / 409 는 status 만으로는 못 가르고
 * message 로 이름 충돌(새 소유자에게 같은 이름의 프로젝트)과 DB 잠금 경합을 가른다 — 그래서 message 를 그대로 싣는다.
 */
export class OwnerMigrationApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "OwnerMigrationApiError";
  }
}

/** 실패 wire `{ status: "Failed", message }` 의 message — 본문이 JSON 이 아니면 status 기반 기본 문구. */
async function readFailureMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string };
    if (body?.message) return body.message;
  } catch {
    // 에러 본문이 JSON 이 아니어도 status 기반 분기는 유지한다
  }
  return `소유권 이전 요청 실패 (${res.status})`;
}

/**
 * 새 소유자(가입자 이메일)에게 소유권을 넘긴다. 성공 본문은 봉투 status 만 확인하고 data 는 쓰지 않는다.
 * 재시도는 하지 않는다 — 이관은 되풀이하면 두 번째 호출이 404(더는 주인이 아님)로 끝나, 네트워크 지연 뒤
 * 재시도가 실제로 성공한 이전을 실패로 보이게 한다.
 */
export async function transferProjectOwner(
  projectId: number,
  newOwnerEmail: string
): Promise<void> {
  const token = await getIdToken();
  const res = await fetch(`${backendBaseUrl()}/projects/${projectId}/migration`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: JSON.stringify({ new_owner_email: newOwnerEmail }),
  });

  if (!res.ok) {
    throw new OwnerMigrationApiError(res.status, await readFailureMessage(res));
  }

  // 200 이어도 봉투 status 가 "Failed" 면 방어적으로 실패로 본다(본문이 없거나 JSON 이 아니면 성공).
  let envelope: { status?: string } | null = null;
  try {
    envelope = (await res.json()) as { status?: string } | null;
  } catch {
    envelope = null;
  }
  if (envelope?.status !== undefined && envelope.status !== "Success") {
    throw new OwnerMigrationApiError(res.status, `unexpected envelope status: ${envelope.status}`);
  }
}
